import React, { useEffect, useRef, useState } from "react";
import AOS from "aos";
import "aos/dist/aos.css";
import * as FaIcons from "react-icons/fa";
import {
  FaTools,
  FaBoxOpen,
  FaBuilding,
  FaUsers,
  FaArrowRight,
  FaPhoneAlt,
  FaCheckCircle,
  FaStar,
  FaLaptop,
  FaChartLine,
  FaShieldAlt,
  FaHeadset,
  FaImage,
  FaYoutube,
  FaChevronLeft,
  FaChevronRight,
} from "react-icons/fa";

import "./Home.css";
import { BASE_URL } from "../../Config/config";
/* =========================================================
   API
========================================================= */

const PRODUCTS_API = `${BASE_URL}/products.php`;

/* =========================================================
   PRODUCT ICON
========================================================= */

const DEFAULT_PRODUCT_ICON = "FaBoxOpen";

const DynamicFaIcon = ({ name, ...props }) => {
  const IconComponent =
    FaIcons[name] || FaIcons[DEFAULT_PRODUCT_ICON] || FaIcons.FaBoxOpen;

  return <IconComponent {...props} />;
};

/* =========================================================
   PRODUCT SLUG
========================================================= */

const createSlug = (name) => {
  return String(name || "")
    .toLowerCase()
    .trim()
    .replace(/&/g, "and")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
};

/* =========================================================
   COMPONENT
========================================================= */

export default function Home() {
  const teamScrollRef = useRef(null);

  /* =======================================================
     HERO
  ======================================================= */

  const [heroIndex, setHeroIndex] = useState(0);

  const [heroImages, setHeroImages] = useState([]);

  const [heroLoading, setHeroLoading] = useState(true);

  /* =======================================================
     TEAM
  ======================================================= */

  const [teamMembers, setTeamMembers] = useState([]);

  const [teamLoading, setTeamLoading] = useState(true);

  /* =======================================================
     PRODUCTS
  ======================================================= */

  const [products, setProducts] = useState([]);

  const [productsLoading, setProductsLoading] = useState(true);
  /* =======================================================
   YOUTUBE VIDEOS
======================================================= */

  const youtubeScrollRef = useRef(null);

  const [youtubeVideos, setYoutubeVideos] = useState([]);
  const [youtubeLoading, setYoutubeLoading] = useState(true);
  const [showAllYoutube, setShowAllYoutube] = useState(false);
  /* =======================================================
     STATS
  ======================================================= */
  const [companyPartners, setCompanyPartners] = useState([]);
  const [companyPartnersLoading, setCompanyPartnersLoading] = useState(true);
  const fetchCompanyPartners = async () => {
    try {
      setCompanyPartnersLoading(true);

      const response = await fetch(
        `${BASE_URL}/company_customers.php?action=get`,
      );

      if (!response.ok) {
        throw new Error(`Company partner API returned ${response.status}`);
      }

      const result = await response.json();

      if (result.status) {
        setCompanyPartners(Array.isArray(result.data) ? result.data : []);
      } else {
        console.error(result.message || "Failed to fetch company partners.");

        setCompanyPartners([]);
      }
    } catch (error) {
      console.error("Fetch Company Partners Error:", error);

      setCompanyPartners([]);
    } finally {
      setCompanyPartnersLoading(false);
    }
  };
  const [dashboardCounts, setDashboardCounts] = useState({
    products: 0,
    sub_products: 0,
    product_modules: 0,
    services: 0,
    modules: 0,
    module_categories: 0,
    team: 0,
    gallery: 0,
    company_clients: 0,
    youtube_videos: 0,
    homepage_images: 0,
    inquiries: 0,
  });

  const [statsLoading, setStatsLoading] = useState(true);
  /* =====================================================
   FETCH DASHBOARD COUNTS
===================================================== */

  const fetchDashboardStats = async () => {
    try {
      setStatsLoading(true);

      const response = await fetch(
        `${BASE_URL}/dashboard.php?action=get_dashboard_stats`,
      );

      if (!response.ok) {
        throw new Error(`Dashboard statistics API returned ${response.status}`);
      }

      const text = await response.text();

      let result;

      try {
        result = JSON.parse(text);
      } catch (error) {
        console.error("Invalid dashboard API response:", text);
        throw new Error("Invalid dashboard API response.");
      }

      if (result.status && result.data?.counts) {
        setDashboardCounts({
          products: Number(result.data.counts.products || 0),
          sub_products: Number(result.data.counts.sub_products || 0),
          product_modules: Number(result.data.counts.product_modules || 0),
          services: Number(result.data.counts.services || 0),
          modules: Number(result.data.counts.modules || 0),
          module_categories: Number(result.data.counts.module_categories || 0),
          team: Number(result.data.counts.team || 0),
          gallery: Number(result.data.counts.gallery || 0),
          company_clients: Number(result.data.counts.company_clients || 0),
          youtube_videos: Number(result.data.counts.youtube_videos || 0),
          homepage_images: Number(result.data.counts.homepage_images || 0),
          inquiries: Number(result.data.counts.inquiries || 0),
        });
      } else {
        console.error(
          result.message || "Failed to fetch dashboard statistics.",
        );
      }
    } catch (error) {
      console.error("Fetch Dashboard Stats Error:", error);
    } finally {
      setStatsLoading(false);
    }
  };

  /* =====================================================
     COMPANY LOGOS
  ===================================================== */

  /* =====================================================
     FETCH PRODUCTS
  ===================================================== */

  const fetchProducts = async () => {
    try {
      setProductsLoading(true);

      const response = await fetch(`${PRODUCTS_API}?action=get_products`);

      if (!response.ok) {
        throw new Error(`Product API returned ${response.status}`);
      }

      const text = await response.text();

      let result;

      try {
        result = JSON.parse(text);
      } catch (error) {
        console.error("Invalid product API response:", text);

        throw new Error("Invalid product API response.");
      }

      if (!result.status) {
        throw new Error(result.message || "Failed to fetch products.");
      }

      const apiProducts = Array.isArray(result.data) ? result.data : [];

      /* ---------------------------------------------------
         ONLY ACTIVE PRODUCTS
      --------------------------------------------------- */

      const activeProducts = apiProducts
        .filter((item) => Boolean(Number(item.status)))
        .map((item) => ({
          ...item,

          name: item.name || "",

          description:
            item.description || "Complete business software solution.",

          // FETCH ICON DIRECTLY FROM DATABASE
          icon: item.icon || DEFAULT_PRODUCT_ICON,

          slug: createSlug(item.name),

          modules: Array.isArray(item.modules)
            ? item.modules.map((module) => ({
                ...module,

                name: module.name || "",

                description: module.description || "",

                // FETCH MODULE ICON FROM DATABASE
                icon: module.icon || "FaLayerGroup",

                subcategories: Array.isArray(module.subcategories)
                  ? module.subcategories
                  : [],
              }))
            : [],
        }));

      setProducts(activeProducts);
    } catch (error) {
      console.error("Fetch Products Error:", error);

      setProducts([]);
    } finally {
      setProductsLoading(false);
    }
  };

  /* =====================================================
     FETCH TEAM MEMBERS
  ===================================================== */

  const fetchTeamMembers = async () => {
    try {
      setTeamLoading(true);

      const response = await fetch(`${BASE_URL}/team.php?action=get`);

      const text = await response.text();

      let result;

      try {
        result = JSON.parse(text);
      } catch (error) {
        console.error("Invalid team API response:", text);

        return;
      }

      if (result.status) {
        setTeamMembers(Array.isArray(result.data) ? result.data : []);
      } else {
        console.error(result.message || "Failed to fetch team members.");

        setTeamMembers([]);
      }
    } catch (error) {
      console.error("Fetch team members error:", error);

      setTeamMembers([]);
    } finally {
      setTeamLoading(false);
    }
  };

  /* =====================================================
     GET TEAM IMAGE URL
  ===================================================== */

  const getTeamImageUrl = (image) => {
    if (!image) {
      return "";
    }

    if (image.startsWith("http://") || image.startsWith("https://")) {
      return image;
    }

    return `${BASE_URL}/${image.replace(/^\/+/, "")}`;
  };

  /* =====================================================
     FETCH HOMEPAGE HERO IMAGES
  ===================================================== */

  const fetchHeroImages = async () => {
    try {
      setHeroLoading(true);

      const response = await fetch(
        `${BASE_URL}/homepage_images.php?action=get`,
      );

      const result = await response.json();

      if (result.status) {
        setHeroImages(result.data || []);

        setHeroIndex(0);
      } else {
        console.error("Failed to fetch hero images:", result.message);
      }
    } catch (error) {
      console.error("Fetch Hero Images Error:", error);
    } finally {
      setHeroLoading(false);
    }
  };

  /* =====================================================
     INITIAL API LOAD
  ===================================================== */

  useEffect(() => {
    fetchProducts();

    fetchTeamMembers();

    fetchHeroImages();

    fetchCompanyPartners();

    fetchDashboardStats();
  }, []);
  const stats = [
    {
      // number: `${dashboardCounts.company_clients}+`,
      number: `2000+`,
      title: "Happy Clients",
      icon: <FaUsers />,
    },
    {
      number: "10+",
      title: "Years of Experience",
      icon: <FaStar />,
    },
    {
      number: `${dashboardCounts.modules}+`,
      title: "Modules Available",
      icon: <FaCheckCircle />,
    },
    {
      number: "24/7",
      title: "Support Available",
      icon: <FaHeadset />,
    },
    {
      number: "98%",
      title: "Client Satisfaction",
      icon: <FaStar />,
    },
  ];

  const getCompanyPartnerImageUrl = (image) => {
    if (!image) {
      return "";
    }

    if (image.startsWith("http://") || image.startsWith("https://")) {
      return image;
    }

    return `${BASE_URL}/${image.replace(/^\/+/, "")}`;
  };

  /* =====================================================
     GET HERO IMAGE URL
  ===================================================== */

  const getHeroImageUrl = (image) => {
    if (!image) {
      return "";
    }

    if (image.startsWith("http://") || image.startsWith("https://")) {
      return image;
    }

    return `${BASE_URL}/${image.replace(/^\/+/, "")}`;
  };

  /* =====================================================
     AUTO HERO SLIDER
  ===================================================== */

  useEffect(() => {
    if (heroImages.length <= 1) {
      return;
    }

    const interval = setInterval(() => {
      setHeroIndex((prev) => {
        const nextIndex = prev + 1;

        if (nextIndex >= heroImages.length) {
          return 0;
        }

        return nextIndex;
      });
    }, 4000);

    return () => {
      clearInterval(interval);
    };
  }, [heroImages.length]);
  /* =====================================================
   FETCH YOUTUBE VIDEOS
===================================================== */

  const fetchYoutubeVideos = async () => {
    try {
      setYoutubeLoading(true);

      const response = await fetch(
        `${BASE_URL}/youtube.php?action=get_youtube_videos`,
      );

      if (!response.ok) {
        throw new Error(`YouTube API returned ${response.status}`);
      }

      const text = await response.text();

      let result;

      try {
        result = JSON.parse(text);
      } catch (error) {
        console.error("Invalid YouTube API response:", text);

        throw new Error("Invalid YouTube API response.");
      }

      if (result.status) {
        setYoutubeVideos(Array.isArray(result.data) ? result.data : []);
      } else {
        console.error(result.message || "Failed to fetch YouTube videos.");

        setYoutubeVideos([]);
      }
    } catch (error) {
      console.error("Fetch YouTube videos error:", error);

      setYoutubeVideos([]);
    } finally {
      setYoutubeLoading(false);
    }
  };
  useEffect(() => {
    fetchYoutubeVideos();
  }, []);
  /* =====================================================
   YOUTUBE HORIZONTAL SCROLL
===================================================== */
  const scrollYoutube = (direction) => {
    const slider = youtubeScrollRef.current;

    if (!slider) return;

    const card = slider.querySelector(".homeYoutubeCard");

    if (!card) return;

    const cardWidth = card.getBoundingClientRect().width;

    const gap = 24;

    const scrollAmount = cardWidth + gap;

    slider.scrollBy({
      left: direction === "left" ? -scrollAmount : scrollAmount,
      behavior: "smooth",
    });
  };
  /* =====================================================
     RENDER
  ===================================================== */
  useEffect(() => {
    AOS.init({
      duration: 800,
      easing: "ease-out-cubic",
      once: true,
      offset: 100,
      delay: 0,
    });

    return () => {
      AOS.refreshHard();
    };
  }, []);
  return (
    <main className="home-page">
      {/* =====================================================
          HERO SECTION
      ===================================================== */}

      <section className="hero-section">
        <div className="hero-container">
          <div className="hero-content" data-aos="fade-right">
            <div className="hero-badge">
              <FaStar />

              <span>Welcome to Star Computers</span>
            </div>

            <h1>
              Smart Software Solutions
              <br />
              for <span>Smarter Businesses</span>
            </h1>

            <p className="hero-description">
              Empowering businesses with Tally, ERP, HRMS and custom software
              solutions to simplify operations, improve accuracy and drive
              growth.
            </p>

            <div className="hero-features">
              <div className="hero-feature">
                <div className="feature-icon">
                  <FaShieldAlt />
                </div>

                <div>
                  <strong>Trusted</strong>

                  <span>by 2000+ Clients</span>
                </div>
              </div>

              <div className="hero-feature">
                <div className="feature-icon">
                  <FaTools />
                </div>

                <div>
                  <strong>Expert</strong>

                  <span>Support Team</span>
                </div>
              </div>

              <div className="hero-feature">
                <div className="feature-icon">
                  <FaBuilding />
                </div>

                <div>
                  <strong>Business</strong>

                  <span>Growth Partner</span>
                </div>
              </div>
            </div>

            <div className="hero-buttons">
              <a href="#products" className="hero-primary-btn">
                Explore Our Solutions
                <FaArrowRight />
              </a>

              <a href="/contact" className="hero-secondary-btn">
                <FaPhoneAlt />
                Talk to an Expert
              </a>
            </div>
          </div>

          {/* =================================================
              HERO IMAGE
          ================================================= */}

          <div
            className="hero-visual"
            data-aos="fade-left"
            data-aos-delay="200"
          >
            {heroLoading ? (
              <div className="hero-image-loading">Loading...</div>
            ) : heroImages.length > 0 ? (
              <div
                className="hero-image-slider"
                style={{
                  transform: `translateX(-${heroIndex * 100}%)`,
                }}
              >
                {heroImages.map((item, index) => (
                  <div className="hero-slide" key={item.id || index}>
                    <img
                      src={getHeroImageUrl(item.image)}
                      alt={`Star Computers ${index + 1}`}
                    />
                  </div>
                ))}
              </div>
            ) : (
              <div className="hero-image-empty">
                <FaImage />

                <span>No homepage images available</span>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* =====================================================
          PRODUCTS SECTION
      ===================================================== */}

      <section className="products-section" id="products">
        <div className="section-container">
          <div className="section-heading" data-aos="fade-up">
            <span className="section-label">OUR SOLUTIONS</span>

            <h2>
              Software Solutions That
              <span> Work For You</span>
            </h2>

            <p>
              Choose the right software solution to improve productivity,
              simplify operations and grow your business.
            </p>
          </div>

          {/* ===============================================
              PRODUCT LOADING
          =============================================== */}

          {productsLoading ? (
            <div className="products-home-loading">Loading products...</div>
          ) : products.length === 0 ? (
            <div className="products-home-empty">
              <FaBoxOpen />

              <h3>No Products Available</h3>

              <p>
                Products will appear here once they are added from the admin
                panel.
              </p>
            </div>
          ) : (
            <div className="products-grid">
              {products.map((product, index) => (
                <div
                  className="product-card"
                  key={product.id || `${product.name}-${index}`}
                  data-aos="fade-up"
                  data-aos-delay={index * 100}
                >
                  <div className="product-icon">
                    <DynamicFaIcon name={product.icon} />
                  </div>

                  <h3>{product.name}</h3>

                  <p>{product.description}</p>

                  <a href={`/products/${product.slug}`} className="learn-more">
                    Learn More
                    <FaArrowRight />
                  </a>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* =====================================================
          STATS
      ===================================================== */}

      <section className="stats-section">
        <div className="stats-container">
          {stats.map((stat, index) => (
            <div
              className="stat-item"
              key={index}
              data-aos="zoom-in"
              data-aos-delay={index * 100}
            >
              <div className="stat-icon">{stat.icon}</div>

              <div className="stat-number">
                {statsLoading ? "..." : stat.number}
              </div>

              <div className="stat-title">{stat.title}</div>
            </div>
          ))}
        </div>
      </section>

      {/* =====================================================
          TEAM SECTION
      ===================================================== */}

      <section className="team-section">
        <div className="section-container">
          <div className="section-heading">
            <span className="section-label">OUR TEAM</span>

            <h2>
              Meet Our
              <span> Expert Team</span>
            </h2>

            <p>
              Our experienced professionals work together to provide reliable
              technology and business solutions.
            </p>
          </div>

          <div className="team-slider-wrapper">
            <div className="team-slider" ref={teamScrollRef}>
              <div className="team-track">
                {teamLoading ? (
                  <div className="team-home-loading">
                    Loading team members...
                  </div>
                ) : teamMembers.length === 0 ? (
                  <div className="team-home-empty">
                    <FaImage />

                    <span>No team members available.</span>
                  </div>
                ) : (
                  <>
                    {/* FIRST SET */}

                    {teamMembers.map((member, index) => (
                      <div
                        className="team-card"
                        data-aos="fade-up"
                        data-aos-delay={index * 100}
                      >
                        <div className="team-image-wrapper">
                          <img
                            src={getTeamImageUrl(member.image)}
                            alt={member.name || "Team member"}
                            className="team-image"
                            loading="lazy"
                          />

                          <div className="team-overlay">
                            <div className="team-overlay-content">
                              <h3>{member.name}</h3>

                              <span>{member.designation}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}

                    {/* DUPLICATE SET */}

                    {teamMembers.map((member, index) => (
                      <div
                        className="team-card"
                        key={`team-second-${member.id || index}`}
                      >
                        <div className="team-image-wrapper">
                          <img
                            src={getTeamImageUrl(member.image)}
                            alt={member.name || "Team member"}
                            className="team-image"
                            loading="lazy"
                          />

                          <div className="team-overlay">
                            <div className="team-overlay-content">
                              <h3>{member.name}</h3>

                              <span>{member.designation}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="company-section">
        <div className="section-container">
          <div className="section-heading company-heading" data-aos="fade-up">
            <span className="section-label">OUR TECHNOLOGY CLIENTS</span>

            <h2>
              Trusted
              <span> Technology Clients</span>
            </h2>

            <p>
              We work with leading software and technology companies to deliver
              reliable solutions to our clients.
            </p>
          </div>
        </div>

        {companyPartnersLoading ? (
          <div className="company-logo-loading">
            Loading technology partners...
          </div>
        ) : companyPartners.length > 0 ? (
          <div className="logo-slider">
            <div className="logo-track">
              {/* FIRST SET */}

              {companyPartners.map((company, index) => (
                <div
                  className="company-logo"
                  key={`first-${company.id || index}`}
                >
                  <img
                    src={getCompanyPartnerImageUrl(company.image)}
                    alt={company.name}
                    loading="lazy"
                  />
                </div>
              ))}

              {/* DUPLICATE SET */}

              {companyPartners.map((company, index) => (
                <div
                  className="company-logo"
                  key={`second-${company.id || index}`}
                >
                  <img
                    src={getCompanyPartnerImageUrl(company.image)}
                    alt={company.name}
                    loading="lazy"
                  />
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="company-logo-empty">
            <FaImage />

            <span>Technology Clients will appear here.</span>
          </div>
        )}
      </section>
      {/* =====================================================
          WHY CHOOSE US
      ===================================================== */}

      <section className="why-section">
        <div className="section-container">
          <div className="why-grid">
            <div className="why-content" data-aos="fade-right">
              <span className="section-label">WHY STAR COMPUTERS</span>

              <h2>
                Your Technology Partner
                <span> for Business Growth</span>
              </h2>

              <p>
                We help businesses choose, implement and maintain reliable
                software solutions that make everyday operations easier and more
                efficient.
              </p>

              <div className="why-list">
                <div>
                  <FaCheckCircle />

                  <span>Professional software implementation</span>
                </div>

                <div>
                  <FaCheckCircle />

                  <span>Reliable technical support</span>
                </div>

                <div>
                  <FaCheckCircle />

                  <span>Customized business solutions</span>
                </div>

                <div>
                  <FaCheckCircle />

                  <span>Complete accounting & ERP solutions</span>
                </div>
              </div>

              <a href="#about" className="why-button">
                Know More About Us
                <FaArrowRight />
              </a>
            </div>

            <div className="why-cards" data-aos="fade-left">
              <div className="why-card">
                <div className="why-card-icon">
                  <FaLaptop />
                </div>

                <h3>Modern Technology</h3>

                <p>
                  Modern and reliable software solutions designed for today's
                  businesses.
                </p>
              </div>

              <div className="why-card">
                <div className="why-card-icon gold">
                  <FaTools />
                </div>

                <h3>Expert Support</h3>

                <p>
                  Get professional assistance whenever your business needs it.
                </p>
              </div>

              <div className="why-card">
                <div className="why-card-icon">
                  <FaChartLine />
                </div>

                <h3>Business Growth</h3>

                <p>
                  Technology solutions that help your business work smarter and
                  faster.
                </p>
              </div>

              <div className="why-card">
                <div className="why-card-icon gold">
                  <FaShieldAlt />
                </div>

                <h3>Reliable Solutions</h3>

                <p>
                  Secure, dependable and practical solutions for your business.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
      <section className="homeYoutubeSection">
        <div className="homeYoutubeContainer">
          {/* =====================================================
        HEADER
    ===================================================== */}

          <div className="homeYoutubeHeader">
            <div className="homeYoutubeHeading" data-aos="fade-up">
              <span className="homeYoutubeLabel">OUR VIDEOS</span>

              <h2>
                Watch Our
                <span> Latest Videos</span>
              </h2>

              <p>
                Explore our latest videos, software solutions, tutorials and
                business updates.
              </p>
            </div>
          </div>

          {/* =====================================================
        LOADING
    ===================================================== */}

          {youtubeLoading ? (
            <div className="homeYoutubeLoading">Loading videos...</div>
          ) : youtubeVideos.length === 0 ? (
            /* ===================================================
         EMPTY
      =================================================== */

            <div className="homeYoutubeEmpty">
              <FaYoutube />

              <h3>No Videos Available</h3>

              <p>
                YouTube videos will appear here once they are added from the
                admin panel.
              </p>
            </div>
          ) : (
            /* ===================================================
         VIDEO AREA
      =================================================== */

            <>
              {/* =================================================
            TOP CONTROLS
        ================================================= */}

              <div className="homeYoutubeControls">
                <button
                  type="button"
                  className="homeYoutubeArrow homeYoutubeArrowLeft"
                  onClick={() => scrollYoutube("left")}
                  aria-label="Previous videos"
                >
                  <FaChevronLeft />
                </button>

                <button
                  type="button"
                  className="homeYoutubeArrow homeYoutubeArrowRight"
                  onClick={() => scrollYoutube("right")}
                  aria-label="Next videos"
                >
                  <FaChevronRight />
                </button>
              </div>

              {/* =================================================
            VIDEO ROW
        ================================================= */}

              <div className="homeYoutubeSlider" ref={youtubeScrollRef}>
                {youtubeVideos.map((video, index) => (
                  <div
                    className="homeYoutubeCard"
                    key={video.id || `home-youtube-${index}`}
                  >
                    {/* VIDEO */}
                    <div className="homeYoutubeVideo">
                      <iframe
                        className="youtube-iframe"
                        src={`${video.embedUrl}?controls=1&rel=0&modestbranding=1`}
                        title={video.title || `YouTube Video ${index + 1}`}
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                        loading="lazy"
                      />
                    </div>

                    {/* CONTENT */}
                    <div className="homeYoutubeContent">
                      <h3>{video.title}</h3>

                      {video.youtubeUrl && (
                        <a
                          href={video.youtubeUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="homeYoutubeWatchBtn"
                        >
                          Watch on YouTube
                          <FaArrowRight />
                        </a>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </section>
    </main>
  );
}
